import React, { useRef, useEffect, useState, useId } from 'react';
import { useAuth } from '../../context/AuthContext';
import { isPresignedHttpUrl } from '../../utils/presignedImageUrl';
import { API_BASE_URL } from '../../config/apiBaseUrl';
const TOKEN_STORAGE_KEY = 'mysipur_auth_token';

interface TextOverlay {
  content: string;
  fontSize: number;
  color: string;
  position?: { x: number; y: number };
  alignment?: 'left' | 'center' | 'right';
  fontFamily?: string;
  outlineColor?: string;
  outlineWidth?: number;
}

interface ImageEditorProps {
  imageUrl: string | null;
  /** Load pixels via authenticated API (same-origin to app API) so canvas is not blocked by R2 CORS. */
  imageStorageKey?: string | null;
  pageContent: string;
  textOverlay?: TextOverlay;
  textOverlays?: TextOverlay[]; // Array of all text overlays to draw
  editingOverlayIndex?: number; // Index of overlay being edited (-1 for new)
  /** When true, Save is enabled even with empty text (e.g. for side-picture background-only edits) */
  allowSaveWithoutText?: boolean;
  onSave: (editedImageUrl: string, textOverlay: TextOverlay) => void;
  onSaveMultiple?: (editedImageUrl: string, textOverlays: TextOverlay[]) => void; // For multiple overlays
  onCancel?: () => void;
}

const ImageEditor: React.FC<ImageEditorProps> = ({
  imageUrl,
  imageStorageKey = null,
  pageContent,
  textOverlay: initialTextOverlay,
  textOverlays: allTextOverlays,
  editingOverlayIndex = -1,
  allowSaveWithoutText = false,
  onSave,
  onSaveMultiple,
  onCancel
}) => {
  const { token } = useAuth();
  const formId = useId().replace(/:/g, '');
  // Determine if we're using multiple overlays mode
  const useMultipleOverlays = allTextOverlays !== undefined;
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  
  const [textOverlay, setTextOverlay] = useState<TextOverlay>(() => {
    // If using multiple overlays and editing an existing one
    if (allTextOverlays !== undefined && allTextOverlays && editingOverlayIndex >= 0 && editingOverlayIndex < allTextOverlays.length) {
      return allTextOverlays[editingOverlayIndex];
    }
    // Otherwise use initialTextOverlay or defaults
    if (initialTextOverlay && initialTextOverlay.content) {
      return initialTextOverlay;
    }
    return {
      content: pageContent || '',
      fontSize: initialTextOverlay?.fontSize || 24,
      color: initialTextOverlay?.color || '#000000',
      position: initialTextOverlay?.position || { x: 0.5, y: 0.8 }, // Default position (center-bottom, where wooden block typically is)
      alignment: initialTextOverlay?.alignment || 'center',
      fontFamily: initialTextOverlay?.fontFamily || 'Arial',
      outlineColor: initialTextOverlay?.outlineColor || '#FFFFFF',
      outlineWidth: initialTextOverlay?.outlineWidth || 2
    };
  });

  // Update text content when pageContent changes (but preserve other settings)
  useEffect(() => {
    if (pageContent && (!initialTextOverlay || !initialTextOverlay.content)) {
      setTextOverlay(prev => ({
        ...prev,
        content: pageContent
      }));
    }
  }, [pageContent, initialTextOverlay]);

  // Update styling when initialTextOverlay changes (for session-level styling persistence)
  // This ensures that when navigating between pages, the styling preferences are applied
  useEffect(() => {
    if (initialTextOverlay) {
      setTextOverlay(prev => {
        // Always update styling when initialTextOverlay changes to ensure session-level styling persists
        // Check if content changed (new page) or if any styling property is provided
        const contentChanged = initialTextOverlay.content && initialTextOverlay.content !== prev.content;
        const hasStylingUpdates = initialTextOverlay.fontSize !== undefined || 
                                  initialTextOverlay.color !== undefined ||
                                  initialTextOverlay.alignment !== undefined ||
                                  initialTextOverlay.fontFamily !== undefined ||
                                  initialTextOverlay.outlineColor !== undefined ||
                                  initialTextOverlay.outlineWidth !== undefined;
        
        // Update if content changed (new page) OR if styling is provided (session styling update)
        if (contentChanged || hasStylingUpdates) {
          const updated = {
            ...prev,
            // Always use initialTextOverlay styling if provided, otherwise keep current
            fontSize: initialTextOverlay.fontSize ?? prev.fontSize,
            color: initialTextOverlay.color ?? prev.color,
            alignment: initialTextOverlay.alignment ?? prev.alignment,
            fontFamily: initialTextOverlay.fontFamily ?? prev.fontFamily,
            outlineColor: initialTextOverlay.outlineColor ?? prev.outlineColor,
            outlineWidth: initialTextOverlay.outlineWidth ?? prev.outlineWidth,
            // Update content if provided, otherwise keep current
            content: initialTextOverlay.content || prev.content,
            // Update position if provided, otherwise keep current
            position: initialTextOverlay.position || prev.position
          };

          return updated;
        }
        return prev;
      });
    }
  }, [initialTextOverlay]);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Load and render image with text overlay
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const key = imageStorageKey?.trim();
    if (!key && !imageUrl) return;

    let cancelled = false;
    let blobUrlToRevoke: string | null = null;
    const ac = new AbortController();

    const attachImage = (src: string, setCrossOriginAnonymous: boolean) => {
      const img = new Image();
      if (
        setCrossOriginAnonymous &&
        src &&
        !src.startsWith('blob:') &&
        !src.startsWith('data:')
      ) {
        img.crossOrigin = 'anonymous';
      }

      img.onload = () => {
        if (cancelled) return;
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        if (textOverlay.content) {
          drawTextOverlay(ctx, canvas.width, canvas.height);
        }
        imageRef.current = img;
      };

      img.onerror = () => {
        console.error(
          'ImageEditor: image load failed (URL omitted for security)',
          imageStorageKey ? { storageKey: imageStorageKey } : {}
        );
      };

      img.src = src;
    };

    const run = async () => {
      if (key) {
        const t =
          token ??
          (typeof sessionStorage !== 'undefined'
            ? sessionStorage.getItem(TOKEN_STORAGE_KEY)
            : null);
        if (t) {
          try {
            const res = await fetch(
              `${API_BASE_URL}/images/file?key=${encodeURIComponent(key)}`,
              {
                headers: { Authorization: `Bearer ${t}` },
                signal: ac.signal,
              }
            );
            if (!res.ok) {
              throw new Error(`file ${res.status}`);
            }
            const blob = await res.blob();
            if (cancelled) return;
            blobUrlToRevoke = URL.createObjectURL(blob);
            attachImage(blobUrlToRevoke, false);
            return;
          } catch (e) {
            if ((e as Error).name === 'AbortError') return;
            console.error(
              'ImageEditor: /images/file failed, falling back to imageUrl (URLs omitted)',
              imageStorageKey ? { storageKey: imageStorageKey } : {}
            );
          }
        }
      }

      if (imageUrl) {
        if (isPresignedHttpUrl(imageUrl)) {
          console.error(
            'ImageEditor: private object URL cannot be drawn on canvas (CORS). Use imageStorageKey so the app loads via /api/images/file.',
            imageStorageKey ? { storageKey: imageStorageKey } : {}
          );
          return;
        }
        attachImage(imageUrl, true);
      }
    };

    void run();

    return () => {
      cancelled = true;
      ac.abort();
      if (blobUrlToRevoke) {
        URL.revokeObjectURL(blobUrlToRevoke);
      }
    };
  }, [
    imageUrl,
    imageStorageKey,
    token,
    textOverlay,
    allTextOverlays,
    editingOverlayIndex,
    useMultipleOverlays,
  ]);

  // Helper function to check if font is Hebrew
  const isHebrewFontFamily = (fontFamily: string): boolean => {
    const hebrewFonts = ['David', 'Arial Hebrew', 'Noto Sans Hebrew', 'FrankRuehl', 'Gisha', 'Miriam', 'Rod', 'Simplified Arabic'];
    return hebrewFonts.includes(fontFamily);
  };

  // Helper function to get font fallback
  const getFontFallback = (fontFamily: string): string => {
    if (isHebrewFontFamily(fontFamily)) {
      return 'sans-serif';
    }
    if (fontFamily.includes('serif') || ['Georgia', 'Times New Roman', 'Book Antiqua', 'Palatino Linotype'].includes(fontFamily)) {
      return 'serif';
    }
    if (fontFamily.includes('monospace') || fontFamily === 'Courier New') {
      return 'monospace';
    }
    // Title fonts fallback
    if (['Impact', 'Arial Black', 'Comic Sans MS', 'Trebuchet MS', 'Century Gothic', 'Lucida Sans Unicode', 'Gill Sans'].includes(fontFamily)) {
      return 'Arial Black, Arial, sans-serif';
    }
    return 'sans-serif';
  };

  const drawSingleTextOverlay = (ctx: CanvasRenderingContext2D, canvasWidth: number, canvasHeight: number, overlay: TextOverlay) => {
    const { content, fontSize, color, position, alignment, fontFamily, outlineColor, outlineWidth } = overlay;
    
    // Calculate text position (default to center-bottom if not specified)
    const baseX = position ? position.x * canvasWidth : canvasWidth * 0.5;
    const y = position ? position.y * canvasHeight : canvasHeight * 0.8;

    // Configure text style
    const selectedFont = fontFamily || 'Arial';
    ctx.font = `bold ${fontSize}px "${selectedFont}", ${getFontFallback(selectedFont)}`;
    ctx.fillStyle = color;
    ctx.textBaseline = 'middle';
    ctx.strokeStyle = outlineColor || '#FFFFFF';
    ctx.lineWidth = Math.max(1, outlineWidth || Math.max(2, fontSize * 0.1));
    
    // Set text direction for Hebrew fonts
    const isHebrewFont = isHebrewFontFamily(selectedFont);
    if (isHebrewFont) {
      ctx.direction = 'rtl';
    }

    // Split content by newlines first, then handle word wrapping for each line
    const maxWidth = canvasWidth * 0.6; // Max width for text (60% of canvas)
    const paragraphs = content.split('\n');
    const lines: string[] = [];

    paragraphs.forEach(paragraph => {
      if (paragraph.trim() === '') {
        // Empty line
        lines.push('');
      } else {
        // Handle word wrapping for this paragraph
        const words = paragraph.split(' ');
        let currentLine = '';

        words.forEach(word => {
          const testLine = currentLine + (currentLine ? ' ' : '') + word;
          const metrics = ctx.measureText(testLine);
          
          if (metrics.width > maxWidth && currentLine) {
            lines.push(currentLine);
            currentLine = word;
          } else {
            currentLine = testLine;
          }
        });
        
        if (currentLine) {
          lines.push(currentLine);
        }
      }
    });

    // Draw text with outline for better visibility
    const lineHeight = fontSize * 1.2;
    const startY = y - ((lines.length - 1) * lineHeight) / 2;

    lines.forEach((line, index) => {
      const lineY = startY + (index * lineHeight);
      
      if (line.trim()) {
        // Calculate x position based on alignment
        // For all alignments, use position.x to allow full dragging control
        let x = baseX;
        const textAlign = alignment || 'center';
        
        if (textAlign === 'left') {
          ctx.textAlign = 'left';
          // Use position.x directly - allows dragging anywhere horizontally
          // The text will align to the left at this x position
          x = baseX;
        } else if (textAlign === 'right') {
          ctx.textAlign = 'right';
          // Use position.x directly - allows dragging anywhere horizontally
          // The text will align to the right at this x position
          x = baseX;
        } else {
          ctx.textAlign = 'center';
          x = baseX; // Center alignment uses baseX (position.x)
        }
        
        // Draw stroke (outline) first, only if outlineWidth > 0
        const actualOutlineWidth = outlineWidth !== undefined ? outlineWidth : 2;
        if (actualOutlineWidth > 0) {
          ctx.strokeText(line, x, lineY);
        }
        // Then draw fill
        ctx.fillText(line, x, lineY);
      }
    });
  };

  const drawTextOverlay = (ctx: CanvasRenderingContext2D, canvasWidth: number, canvasHeight: number) => {
    if (useMultipleOverlays && allTextOverlays) {
      // Draw all existing overlays first
      allTextOverlays.forEach((overlay, index) => {
        if (overlay.content && index !== editingOverlayIndex) {
          drawSingleTextOverlay(ctx, canvasWidth, canvasHeight, overlay);
        }
      });
      // Then draw the currently editing overlay (so it appears on top)
      if (textOverlay.content) {
        drawSingleTextOverlay(ctx, canvasWidth, canvasHeight, textOverlay);
      }
    } else {
      // Single overlay mode (backward compatibility)
      if (textOverlay.content) {
        drawSingleTextOverlay(ctx, canvasWidth, canvasHeight, textOverlay);
      }
    }
  };

  const handleFontSizeChange = (newSize: number) => {
    setTextOverlay(prev => ({ ...prev, fontSize: Math.max(12, Math.min(250, newSize)) }));
  };

  const handleColorChange = (newColor: string) => {
    setTextOverlay(prev => ({ ...prev, color: newColor }));
  };

  const handleContentChange = (newContent: string) => {
    setTextOverlay(prev => ({ ...prev, content: newContent }));
  };

  const handleSave = () => {
    if (!canvasRef.current) return;

    setIsLoading(true);
    
    // Convert canvas to blob
    canvasRef.current.toBlob((blob) => {
      if (!blob) {
        setIsLoading(false);
        return;
      }

      // Create object URL from blob
      const editedImageUrl = URL.createObjectURL(blob);
      
      if (useMultipleOverlays && onSaveMultiple && allTextOverlays) {
        // Multiple overlays mode: merge current overlay with all overlays
        let updatedOverlays = [...allTextOverlays];
        if (editingOverlayIndex >= 0 && editingOverlayIndex < updatedOverlays.length) {
          // Update existing overlay
          updatedOverlays[editingOverlayIndex] = textOverlay;
        } else {
          // Add new overlay
          updatedOverlays.push(textOverlay);
        }
        onSaveMultiple(editedImageUrl, updatedOverlays);
      } else {
        // Single overlay mode (backward compatibility)
        onSave(editedImageUrl, textOverlay);
      }
      
      setIsLoading(false);
    }, 'image/png', 0.95);
  };

  // Handle mouse events for dragging text
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current || !textOverlay.content) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    
    setIsDragging(true);
    setDragStart({ x, y });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDragging || !dragStart || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const currentX = (e.clientX - rect.left) * scaleX;
    const currentY = (e.clientY - rect.top) * scaleY;
    
    const deltaX = currentX - dragStart.x;
    const deltaY = currentY - dragStart.y;
    
    const { position } = textOverlay;
    if (position) {
      const newX = (position.x * canvas.width + deltaX) / canvas.width;
      const newY = (position.y * canvas.height + deltaY) / canvas.height;
      
      // Clamp values between 0 and 1
      const clampedX = Math.max(0, Math.min(1, newX));
      const clampedY = Math.max(0, Math.min(1, newY));
      
      setTextOverlay(prev => ({
        ...prev,
        position: { x: clampedX, y: clampedY }
      }));
      
      setDragStart({ x: currentX, y: currentY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setDragStart(null);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
    setDragStart(null);
  };

  if (!imageUrl) {
    return (
      <div className="bg-gray-100 rounded-lg p-12 text-center text-gray-500">
        No image available for editing
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Canvas Preview */}
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="flex justify-center">
          <div className="relative">
            <canvas
              ref={canvasRef}
              className="max-w-full h-auto rounded-lg shadow-md cursor-move"
              style={{ maxHeight: '600px' }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
            />
            {isDragging && (
              <div className="absolute top-2 left-2 bg-blue-500 text-white px-3 py-1 rounded text-sm font-medium">
                Dragging text...
              </div>
            )}
          </div>
        </div>
        <p className="text-sm text-gray-600 mt-2 text-center">
          💡 Click and drag the text on the image to move it
        </p>
      </div>

      {/* Text Overlay Controls */}
      <div className="bg-white rounded-lg p-6 border border-gray-200 space-y-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Text Overlay Settings</h3>
        
        {/* Text Content */}
        <div>
          <label htmlFor={`${formId}-text-content`} className="block text-sm font-medium text-gray-700 mb-2">
            Text Content
          </label>
          <textarea
            id={`${formId}-text-content`}
            value={textOverlay.content}
            onChange={(e) => handleContentChange(e.target.value)}
            className="w-full h-32 p-3 border border-gray-300 rounded-lg resize-y focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            placeholder="Enter text to display on the image... (Press Enter for new lines)"
          />
        </div>

        {/* Font Size Control */}
        <div>
          <label htmlFor={`${formId}-font-size`} className="block text-sm font-medium text-gray-700 mb-2">
            Font Size: {textOverlay.fontSize}px
          </label>
          <div className="flex items-center gap-4">
            <input
              id={`${formId}-font-size`}
              type="range"
              min="12"
              max="150"
              value={textOverlay.fontSize}
              onChange={(e) => handleFontSizeChange(parseInt(e.target.value))}
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex gap-2">
              <button
                onClick={() => handleFontSizeChange(textOverlay.fontSize - 2)}
                disabled={textOverlay.fontSize <= 12}
                className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                −
              </button>
              <button
                onClick={() => handleFontSizeChange(textOverlay.fontSize + 2)}
                disabled={textOverlay.fontSize >= 250}
                className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                +
              </button>
            </div>
          </div>
        </div>

        {/* Font Family Selection */}
        <div>
          <label htmlFor={`${formId}-font-family`} className="block text-sm font-medium text-gray-700 mb-2">
            Font Family
          </label>
          <select
            id={`${formId}-font-family`}
            value={textOverlay.fontFamily || 'Arial'}
            onChange={(e) => setTextOverlay(prev => ({ ...prev, fontFamily: e.target.value }))}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          >
            <optgroup label="Book Fonts">
              <option value="Georgia">Georgia (Serif)</option>
              <option value="Times New Roman">Times New Roman (Serif)</option>
              <option value="Book Antiqua">Book Antiqua (Serif)</option>
              <option value="Palatino Linotype">Palatino Linotype (Serif)</option>
              <option value="Courier New">Courier New (Monospace)</option>
            </optgroup>
            <optgroup label="Sans-Serif Fonts">
              <option value="Arial">Arial</option>
              <option value="Helvetica">Helvetica</option>
              <option value="Verdana">Verdana</option>
              <option value="Tahoma">Tahoma</option>
            </optgroup>
            <optgroup label="Title Fonts (Bold & Display)">
              <option value="Impact">Impact (Bold Display)</option>
              <option value="Comic Sans MS">Comic Sans MS (Playful)</option>
              <option value="Arial Black">Arial Black (Bold)</option>
              <option value="Trebuchet MS">Trebuchet MS (Modern Bold)</option>
              <option value="Century Gothic">Century Gothic (Elegant)</option>
              <option value="Lucida Sans Unicode">Lucida Sans Unicode (Clear)</option>
              <option value="Gill Sans">Gill Sans (Classic)</option>
            </optgroup>
            <optgroup label="Hebrew Fonts">
              <option value="David">David (דוד)</option>
              <option value="Arial Hebrew">Arial Hebrew</option>
              <option value="Noto Sans Hebrew">Noto Sans Hebrew</option>
              <option value="FrankRuehl">FrankRuehl (פרנק רוהל)</option>
              <option value="Gisha">Gisha (גישה)</option>
              <option value="Miriam">Miriam (מרים)</option>
              <option value="Rod">Rod (רוד)</option>
            </optgroup>
          </select>
        </div>

        {/* Text Alignment */}
        <fieldset className="border-0 p-0 m-0 min-w-0">
          <legend className="block text-sm font-medium text-gray-700 mb-2 px-0">
            Text Alignment
          </legend>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setTextOverlay(prev => ({ ...prev, alignment: 'left' }))}
              className={`flex-1 px-4 py-3 rounded-lg font-medium transition ${
                (textOverlay.alignment || 'center') === 'left'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              ⬅️ Left
            </button>
            <button
              type="button"
              onClick={() => setTextOverlay(prev => ({ ...prev, alignment: 'center' }))}
              className={`flex-1 px-4 py-3 rounded-lg font-medium transition ${
                (textOverlay.alignment || 'center') === 'center'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              ↔️ Center
            </button>
            <button
              type="button"
              onClick={() => setTextOverlay(prev => ({ ...prev, alignment: 'right' }))}
              className={`flex-1 px-4 py-3 rounded-lg font-medium transition ${
                (textOverlay.alignment || 'center') === 'right'
                  ? 'bg-orange-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              ➡️ Right
            </button>
          </div>
        </fieldset>

        {/* Outline Controls */}
        <fieldset className="border-0 p-0 m-0 min-w-0 space-y-3">
          <legend className="block text-sm font-medium text-gray-700 mb-2 px-0">
            Text Outline
          </legend>
            {/* Outline Color */}
            <div className="flex items-center gap-4">
              <label htmlFor={`${formId}-outline-color`} className="text-sm text-gray-600 w-24 shrink-0">
                Outline Color:
              </label>
              <input
                id={`${formId}-outline-color`}
                type="color"
                value={textOverlay.outlineColor || '#FFFFFF'}
                onChange={(e) => setTextOverlay(prev => ({ ...prev, outlineColor: e.target.value }))}
                className="w-16 h-10 border border-gray-300 rounded cursor-pointer"
              />
              <div className="flex gap-2 flex-wrap">
                {['#FFFFFF', '#000000', '#FF0000', '#0000FF', '#FFFF00'].map((color) => (
                  <button
                    key={color}
                    onClick={() => setTextOverlay(prev => ({ ...prev, outlineColor: color }))}
                    className={`w-8 h-8 rounded border-2 ${
                      (textOverlay.outlineColor || '#FFFFFF') === color ? 'border-gray-800' : 'border-gray-300'
                    }`}
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
              <input
                id={`${formId}-outline-color-hex`}
                type="text"
                value={textOverlay.outlineColor || '#FFFFFF'}
                onChange={(e) => setTextOverlay(prev => ({ ...prev, outlineColor: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono w-24"
                placeholder="#FFFFFF"
                aria-label="Outline color hex value"
              />
            </div>
            {/* Outline Width */}
            <div className="flex items-center gap-4">
              <label htmlFor={`${formId}-outline-width`} className="text-sm text-gray-600 w-24 shrink-0">
                Outline Width:
              </label>
              <input
                id={`${formId}-outline-width`}
                type="range"
                min="0"
                max="10"
                value={textOverlay.outlineWidth !== undefined ? textOverlay.outlineWidth : 2}
                onChange={(e) => setTextOverlay(prev => ({ ...prev, outlineWidth: parseInt(e.target.value) }))}
                className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <span className="text-sm text-gray-700 w-12 text-right">
                {textOverlay.outlineWidth !== undefined ? textOverlay.outlineWidth : 2}px
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setTextOverlay(prev => {
                    const currentWidth = prev.outlineWidth !== undefined ? prev.outlineWidth : 2;
                    return { ...prev, outlineWidth: Math.max(0, currentWidth - 1) };
                  })}
                  disabled={(textOverlay.outlineWidth !== undefined ? textOverlay.outlineWidth : 2) <= 0}
                  className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  −
                </button>
                <button
                  onClick={() => setTextOverlay(prev => {
                    const currentWidth = prev.outlineWidth !== undefined ? prev.outlineWidth : 2;
                    return { ...prev, outlineWidth: Math.min(10, currentWidth + 1) };
                  })}
                  disabled={(textOverlay.outlineWidth !== undefined ? textOverlay.outlineWidth : 2) >= 10}
                  className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  +
                </button>
              </div>
            </div>
        </fieldset>

        {/* Color Picker */}
        <div>
          <label htmlFor={`${formId}-text-color`} className="block text-sm font-medium text-gray-700 mb-2">
            Text Color
          </label>
          <div className="flex items-center gap-4">
            <input
              id={`${formId}-text-color`}
              type="color"
              value={textOverlay.color}
              onChange={(e) => handleColorChange(e.target.value)}
              className="w-16 h-10 border border-gray-300 rounded cursor-pointer"
            />
            <div className="flex gap-2 flex-wrap">
              {['#000000', '#FFFFFF', '#FF0000', '#0000FF', '#00FF00', '#FFFF00', '#FF00FF'].map((color) => (
                <button
                  key={color}
                  onClick={() => handleColorChange(color)}
                  className={`w-10 h-10 rounded border-2 ${
                    textOverlay.color === color ? 'border-gray-800' : 'border-gray-300'
                  }`}
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
            </div>
            <input
              type="text"
              value={textOverlay.color}
              onChange={(e) => handleColorChange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
              placeholder="#000000"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4 border-t border-gray-200">
          {onCancel && (
            <button
              onClick={onCancel}
              className="px-6 py-2 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-medium transition"
            >
              Cancel
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={isLoading || (!allowSaveWithoutText && !textOverlay.content)}
            className="px-6 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium transition flex-1"
          >
            {isLoading ? 'Saving...' : 'Save Edited Image'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageEditor;





